import { Component, ChangeDetectionStrategy } from '@angular/core';
import { MultiselectObjectManagerComponentBase } from '../../../core/component/multiselect-object-manager/multiselect-object-manager.component';
import { FilterDefinitionItem } from '../../../core/model/filter-definition-item';
import { MenuItemDefinition } from '../../../core/model/menu-item-definition';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { propertyCommand } from '../../command/property.command';
import { Property } from '../../model/property';
import { PropertyCommandHandlerResolver } from '../../service/command/property-command-handler-resolver.service';
import { ColumnDefinitionService } from '../../service/data/column-definition.service';
import { PropertyService } from '../../service/data/property.service';
import { CompanyService } from '../../service/data/company.service';
import { UserAccountService } from '../../../common/service/user-account.service';
import { Features, ModuleNames } from '../../../common/model/account-definition';
import { DimensionLabelsService } from '../../../accounting/service/data/dimension-labels.service';
import { DimensionLabels } from '../../../accounting/model/dimension-labels';
import { CustomPropertyService } from '../../service/data/custom-property.service';
import { CustomPropertyHelper } from '../../helper/customproperties-helper';
import { CustomPropertyVisibilityType } from '../../model/const/enums';
import { forkJoin } from 'rxjs';
import ListViewColumn from '../../../core/model/list-view-column';
import { ColumnDefinition } from '../../../core/model/column-definition';

@Component({
    selector: 'app-property-manager',
    templateUrl: './property-manager.component.html',
    styleUrls: ['./../../../core/component/multiselect-object-manager/multiselect-object-manager.component.scss'],
    providers: [PropertyCommandHandlerResolver],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PropertyManagerComponent extends MultiselectObjectManagerComponentBase<Property> {
  menuDefinition: MenuItemDefinition[];
  excelColumnDefinition: ColumnDefinition<Property>[] = [];
  excelContext = 'Property';
  excelFileName = 'Fastigheter';
  selectedColumns: ListViewColumn<Property>[] = [];
  selectedPropertyIds: string[] = [];
  private pendingSelectedColumns: ListViewColumn<Property>[] = [];
  private columnUpdateScheduled = false;
  constructor(
    private readonly columnDefinitionService: ColumnDefinitionService,
    private readonly _dataService: PropertyService,
    private readonly _companyService: CompanyService,
    private readonly _dimensionLabelsService: DimensionLabelsService,
    private readonly _customPropertyService: CustomPropertyService,
    private readonly _customPropertyHelper: CustomPropertyHelper,
    dispatcher: CommandDispatcher,
    handlerResolver: PropertyCommandHandlerResolver,
    messageService: MessageService,
    navigationService: NavigationService,
    private _userAccountService: UserAccountService
  ) {
    super(_dataService, dispatcher, handlerResolver, messageService, navigationService);

    this.title = 'PROPERTY.LIST_HEADER';
    this.columnDefinition = this.columnDefinitionService.create('Property');
    this.excelColumnDefinition = [...this.columnDefinition];

    this.menuDefinition = [];
    this.preferencesKey = 'rent.property-manager.list';

    this.AddSubscription(
      _userAccountService.current.subscribe({
        next: account => {
          if (account.hasAccess(ModuleNames.Properties, Features.EditProperty)) {
            this.addContextMenuItemDefinition('PROPERTY.MOVE_TO_COMPANY', propertyCommand.moveToCompany);
            this.addContextMenuItemDefinition('PROPERTY_TAX.MENU_ITEM_TITLE', propertyCommand.propertyTaxRental);
          }
        },
      })
    );
  }

  ngOnInit() {
    this.AddSubscription(this._dimensionLabelsService.getDefaultLabels().subscribe(labels => this.applyDimensionLabels(labels)));

    this.AddSubscription(
      forkJoin([this._dataService.getFilterDefinition(), this._customPropertyService.getList()]).subscribe(([definition, customProperties]) => {
        this.defaultFilters = definition.defaultFields;

        const baseFilters = [
          new FilterDefinitionItem(
            'COMMON.COMPANY',
            'company',
            definition.company,
            this._companyService,
            definition.selectedFields.includes('company')
          ),
          new FilterDefinitionItem(
            'PROPERTY.NAME',
            'propertyName',
            definition.propertyName,
            null,
            definition.selectedFields.includes('propertyName')
          ),
          new FilterDefinitionItem(
            'PROPERTY.NUMBER',
            'propertyNumber',
            definition.propertyNumber,
            null,
            definition.selectedFields.includes('propertyNumber')
          ),
          new FilterDefinitionItem(
            'PROPERTY.MUNICIPALITY',
            'municipality',
            definition.municipality,
            null,
            definition.selectedFields.includes('municipality')
          ),
        ];

        const customPropertyFilters = this._customPropertyHelper.generateCustomPropertyFilters(
          customProperties,
          definition,
          CustomPropertyVisibilityType.Property
        );
        this.filterDefinition = [...baseFilters, ...customPropertyFilters];
        this._customPropertyHelper.createCustomPropertyColumnDefinition(
          customProperties,
          this.columnDefinition,
          CustomPropertyVisibilityType.Property
        );
        this.excelColumnDefinition = [...this.columnDefinition];
      })
    );

    this.addEditDeleteCommands();
  }

  override filterInternal(filterState: any) {
    this._customPropertyHelper.applyCustomPropertyFilters(filterState);
    this._dimensionLabelsService.getForCompany(filterState?.company).subscribe(labels => this.applyDimensionLabels(labels));
  }

  get excelReady(): boolean {
    return this.selectedColumns.length > 0;
  }

  get selectedPropertyCount(): number {
    return this.selectedPropertyIds.length;
  }

  get allPropertiesSelected(): boolean {
    return this.selectedPropertyCount > 0 && this.selectedPropertyCount === this.itemsSource?.length;
  }

  get totalProperties(): number {
    return this.itemsSource?.length ?? 0;
  }

  onColumnChange(columns: ListViewColumn<Property>[]): void {
    this.pendingSelectedColumns = columns.filter(column => column.selected);
    if (this.columnUpdateScheduled) return;

    this.columnUpdateScheduled = true;
    queueMicrotask(() => {
      this.columnUpdateScheduled = false;
      this.selectedColumns = this.pendingSelectedColumns;
    });
  }

  onSelectedPropertiesChange(properties: Property[]): void {
    this.selectedPropertyIds = properties.map(property => property.id);
  }

  override onFilterChange(filterState: any): void {
    this.selectedPropertyIds = [];
    super.onFilterChange(filterState);
  }

  private applyDimensionLabels(labels: DimensionLabels) {
    this._dimensionLabelsService.setColumnCaption(this.columnDefinition, 'costPlace', labels.costPlaceLabel);
    this._dimensionLabelsService.setColumnCaption(this.columnDefinition, 'costBearer', labels.costBearerLabel);
    this.columnDefinition = [...this.columnDefinition];
  }
}
